import { useMemo } from 'react';
import {
  addDays,
  expandRecurrence,
  firstMatchOnOrAfter,
  WEEKDAY_NAMES,
  WEEKDAY_SHORT,
  weekdayOf,
  type Recurrence,
  type Weekday,
} from '../../shared/recurrence';
import { dayLabel } from '../format';

export interface ScheduleValues {
  start_date: string;
  repeat: boolean;
  freq: string;
  weekdays: string;
  nth: string;
  weekday: string;
  monthday: string;
  until_date: string;
  hide_address: boolean;
  reveal_mode: string;
  reveal_date: string;
  reveal_time: string;
}

type Setter = <K extends keyof ScheduleValues>(k: K, v: ScheduleValues[K]) => void;

/** Same mapping as the server's ruleFromFields; null when incomplete. */
export function ruleFrom(v: ScheduleValues): Recurrence | null {
  if (v.freq === 'weekly' || /^every[234]$/.test(v.freq)) {
    const weekdays = v.weekdays.split(',').filter((x) => /^[0-6]$/.test(x)).map(Number) as Weekday[];
    return { freq: 'weekly', interval: (v.freq === 'weekly' ? 1 : Number(v.freq.slice(-1))) as 1 | 2 | 3 | 4, weekdays };
  }
  if (v.freq === 'monthly_nth') return { freq: 'monthly_nth', nth: Number(v.nth) as 1 | 2 | 3 | 4 | -1, weekday: Number(v.weekday) as Weekday };
  if (v.freq === 'monthly_day') return { freq: 'monthly_day', day: Number(v.monthday) };
  return null;
}

/** Sensible repeat defaults derived from the chosen first date. */
export function repeatDefaults(startDate: string): Partial<ScheduleValues> {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return { freq: 'weekly' };
  const wd = weekdayOf(startDate);
  const day = Number(startDate.slice(8, 10));
  return {
    freq: 'weekly',
    weekdays: String(wd),
    nth: String(Math.min(4, Math.ceil(day / 7))),
    weekday: String(wd),
    monthday: String(day),
    until_date: addDays(startDate, 91),
  };
}

const NTH_OPTIONS = [
  ['1', '1st'],
  ['2', '2nd'],
  ['3', '3rd'],
  ['4', '4th'],
  ['-1', 'last'],
] as const;

export function RepeatSection({ v, set, err }: { v: ScheduleValues; set: Setter; err: (k: string) => string | undefined }) {
  const rule = v.repeat ? ruleFrom(v) : null;
  const preview = useMemo(() => (rule && v.start_date && v.until_date ? expandRecurrence(v.start_date, rule, v.until_date) : null), [rule, v.start_date, v.until_date]);
  const snap = preview?.error && rule && v.start_date ? firstMatchOnOrAfter(v.start_date, rule) : null;
  const weekly = v.freq === 'weekly' || /^every[234]$/.test(v.freq);
  const days = new Set(v.weekdays.split(',').filter(Boolean));
  const maxUntil = v.start_date ? addDays(v.start_date, 366) : undefined;

  return (
    <div>
      <label id="repeat-l">Does it repeat?</label>
      <div className="segmented" role="group" aria-labelledby="repeat-l">
        <button type="button" aria-pressed={!v.repeat} onClick={() => set('repeat', false)}>
          One time
        </button>
        <button
          type="button"
          aria-pressed={v.repeat}
          onClick={() => {
            set('repeat', true);
            const d = repeatDefaults(v.start_date);
            for (const [k, val] of Object.entries(d)) if (!v[k as keyof ScheduleValues]) set(k as keyof ScheduleValues, val as never);
          }}
        >
          Repeats
        </button>
      </div>

      {v.repeat && (
        <div className="panel">
          <label htmlFor="freq">How often</label>
          <select id="freq" value={v.freq} onChange={(e) => set('freq', e.target.value)} aria-invalid={err('freq') ? true : undefined}>
            <option value="weekly">Every week</option>
            <option value="every2">Every 2 weeks</option>
            <option value="every3">Every 3 weeks</option>
            <option value="every4">Every 4 weeks</option>
            <option value="monthly_nth">Monthly, on a day of the week (like the 2nd Saturday)</option>
            <option value="monthly_day">Monthly, on a date (like the 15th)</option>
          </select>

          {weekly && (
            <>
              <label id="days-l">On</label>
              <div className="days" role="group" aria-labelledby="days-l">
                {WEEKDAY_SHORT.map((d, i) => (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={days.has(String(i))}
                    aria-label={WEEKDAY_NAMES[i]}
                    onClick={() => {
                      const next = new Set(days);
                      if (next.has(String(i))) next.delete(String(i));
                      else next.add(String(i));
                      set('weekdays', [...next].sort().join(','));
                    }}
                  >
                    {d[0]}
                  </button>
                ))}
              </div>
              {err('weekdays') && <p className="error">{err('weekdays')}</p>}
            </>
          )}

          {v.freq === 'monthly_nth' && (
            <div className="row">
              <div>
                <label htmlFor="nth">Which</label>
                <select id="nth" value={v.nth} onChange={(e) => set('nth', e.target.value)}>
                  {NTH_OPTIONS.map(([val, lbl]) => (
                    <option key={val} value={val}>
                      {lbl}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="weekday">Day</label>
                <select id="weekday" value={v.weekday} onChange={(e) => set('weekday', e.target.value)}>
                  {WEEKDAY_NAMES.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {v.freq === 'monthly_day' && (
            <>
              <label htmlFor="monthday">Day of the month</label>
              <select id="monthday" value={v.monthday} onChange={(e) => set('monthday', e.target.value)}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              {Number(v.monthday) > 28 && <p className="hint">Months without that date are skipped.</p>}
            </>
          )}

          <label htmlFor="until_date">Last date</label>
          <input
            id="until_date"
            type="date"
            min={v.start_date || undefined}
            max={maxUntil}
            value={v.until_date}
            onChange={(e) => set('until_date', e.target.value)}
            aria-invalid={err('until_date') ? true : undefined}
          />
          {err('until_date') && <p className="error">{err('until_date')}</p>}

          <div aria-live="polite">
            {preview?.error ? (
              <p className="error">
                {preview.error}
                {snap && (
                  <>
                    {' '}
                    <button type="button" className="link" onClick={() => set('start_date', snap)}>
                      Use {dayLabel(`${snap}T12:00:00Z`, 'UTC')}
                    </button>
                  </>
                )}
              </p>
            ) : preview ? (
              <>
                <p className="hint" style={{ color: 'var(--ink-2)' }}>
                  {preview.dates.length} {preview.dates.length === 1 ? 'date' : 'dates'}:
                </p>
                <div className="datepills">
                  {preview.dates.slice(0, 12).map((d) => (
                    <span key={d}>{dayLabel(`${d}T12:00:00Z`, 'UTC')}</span>
                  ))}
                  {preview.dates.length > 12 && <span>+{preview.dates.length - 12} more</span>}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export function HideAddressSection({ v, set, err }: { v: ScheduleValues; set: Setter; err: (k: string) => string | undefined }) {
  return (
    <div>
      <label className="check">
        <input type="checkbox" checked={v.hide_address} onChange={(e) => set('hide_address', e.target.checked)} />
        <span>
          Hide the exact address until closer to the sale
          <span className="hint" style={{ display: 'block' }}>
            Shoppers see the town and an approximate area until then. Common for estate sales.
          </span>
        </span>
      </label>
      {v.hide_address && (
        <div className="panel">
          <div className="radio-list" role="radiogroup" aria-label="When to show the address">
            {[
              ['evening_before', 'The evening before the first day (6:00 PM)'],
              ['morning_of', 'The morning of the first day (7:00 AM)'],
              ['custom', 'A date and time I choose'],
            ].map(([val, lbl]) => (
              <label key={val} className="check">
                <input type="radio" name="reveal_mode" value={val} checked={(v.reveal_mode || 'evening_before') === val} onChange={() => set('reveal_mode', val!)} />
                {lbl}
              </label>
            ))}
          </div>
          {v.reveal_mode === 'custom' && (
            <div className="row">
              <div>
                <label htmlFor="reveal_date">Date</label>
                <input id="reveal_date" type="date" value={v.reveal_date} max={v.start_date || undefined} onChange={(e) => set('reveal_date', e.target.value)} aria-invalid={err('reveal_date') ? true : undefined} />
              </div>
              <div>
                <label htmlFor="reveal_time">Time</label>
                <input id="reveal_time" type="time" step={900} value={v.reveal_time} onChange={(e) => set('reveal_time', e.target.value)} aria-invalid={err('reveal_time') ? true : undefined} />
              </div>
            </div>
          )}
          {(err('reveal_date') || err('reveal_time')) && <p className="error">{err('reveal_date') ?? err('reveal_time')}</p>}
        </div>
      )}
    </div>
  );
}
